/**
 * Toast Notification Service
 *
 * A centralized service for displaying toast notifications throughout the application.
 * This service provides a consistent API for showing different types of notifications.
 *
 * Features:
 * - Different notification types (success, error, info, warning)
 * - Consistent styling and behavior
 * - Support for custom options
 *
 * Usage:
 * ```js
 * import { showSuccess, showError } from '../services/toast-service';
 *
 * // Show a success message
 * showSuccess('Operation completed successfully');
 *
 * // Show an error message with custom options
 * showError('Failed to save changes', { autoClose: false });
 * ```
 *
 * @module services/toast-service
 */

import { toast } from "react-toastify";

/**
 * Show a success toast notification
 *
 * @param {string|React.ReactNode} msg - The message to display
 * @param {Object} [options] - Custom options for the toast
 */
export const showSuccess = (msg, options = undefined) =>
  toast.success(msg, options);

/**
 * Show an error toast notification
 *
 * @param {string|React.ReactNode} msg - The message to display
 * @param {Object} [options] - Custom options for the toast
 */
export const showError = (msg, options = undefined) =>
  toast.error(msg, options);

/**
 * Show an info toast notification
 *
 * @param {string|React.ReactNode} msg - The message to display
 * @param {Object} [options] - Custom options for the toast
 */
export const showInfo = (msg, options = undefined) => toast.info(msg, options);

/**
 * Show a warning toast notification
 *
 * @param {string|React.ReactNode} msg - The message to display
 * @param {Object} [options] - Custom options for the toast
 */
export const showWarning = (msg, options = undefined) =>
  toast.warning(msg, options);
